import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { useSession } from "@/lib/session";
import { useConnection } from "@/lib/offline/connection";
import { SyncStatusPanel } from "@/components/offline/sync-status";
import { PackDownloadButton } from "@/components/offline/pack-download-button";
import { listPacks, packsStorageSummary, removePack, downloadPack } from "@/lib/offline/packs";
import { listDownloadableContent, type DownloadableItem } from "@/lib/offline/packs.functions";
import type { ContentPack, PackKind } from "@/lib/offline/db";
import { estimateStorage } from "@/lib/offline/db";
import {
  BookOpen, Video, ClipboardList, ScrollText, FileText, Sparkles,
  HardDrive, Trash2, Loader2, CloudDownload,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/learner/downloads")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Downloads & Offline Learning — MAKAI" },
      { name: "description", content: "Download lessons, videos, quizzes, past papers, assignments and AI notes to keep learning without internet. Work syncs automatically when you reconnect." },
      { property: "og:title", content: "Downloads & Offline Learning — MAKAI" },
      { property: "og:description", content: "Keep learning without internet. MAKAI saves your progress and syncs it when you're back online." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: DownloadsPage,
});

const KINDS: { key: PackKind; label: string; icon: typeof BookOpen }[] = [
  { key: "lesson", label: "Lessons", icon: BookOpen },
  { key: "video", label: "Videos", icon: Video },
  { key: "quiz", label: "Quizzes", icon: ClipboardList },
  { key: "paper", label: "Past papers", icon: ScrollText },
  { key: "assignment", label: "Assignments", icon: FileText },
  { key: "ai_notes", label: "AI notes", icon: Sparkles },
];

function bytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function DownloadsPage() {
  const { session } = useSession();
  const { state } = useConnection();
  const userId = session?.userId;

  const [tab, setTab] = useState<PackKind>("lesson");
  const [catalogue, setCatalogue] = useState<DownloadableItem[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [summary, setSummary] = useState({ count: 0, storedBytes: 0, rawBytes: 0, savedPct: 0 });
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);

  const refreshLocal = useCallback(async () => {
    setPacks(await listPacks());
    setSummary(await packsStorageSummary());
    setStorage(await estimateStorage());
  }, []);

  useEffect(() => {
    void refreshLocal();
    const onChange = () => { void refreshLocal(); };
    window.addEventListener("makai:packs-changed", onChange);
    return () => window.removeEventListener("makai:packs-changed", onChange);
  }, [refreshLocal]);

  useEffect(() => {
    let cancelled = false;
    if (state === "offline") { setLoading(false); return; }
    void (async () => {
      try {
        const items = (await listDownloadableContent()) as DownloadableItem[];
        if (!cancelled) setCatalogue(items);
      } catch {
        /* stay with what's already downloaded */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [state]);

  const visible = useMemo(() => catalogue.filter((c) => c.kind === tab), [catalogue, tab]);
  const savedIds = useMemo(() => new Set(packs.map((p) => `${p.kind}:${p.resource_id}`)), [packs]);

  async function downloadAllVisible() {
    setBulkBusy(true);
    let ok = 0;
    try {
      for (const item of visible.slice(0, 15)) {
        if (savedIds.has(`${item.kind}:${item.id}`)) continue;
        try {
          await downloadPack({ kind: item.kind, resourceId: item.id, userId, includeMedia: item.hasMedia });
          ok++;
        } catch { /* skip failures, keep going */ }
      }
      toast.success(ok ? `${ok} item${ok === 1 ? "" : "s"} saved for offline use` : "Nothing new to download");
    } finally {
      setBulkBusy(false);
      await refreshLocal();
    }
  }

  return (
    <AppShell role={(session?.role ?? "learner") as "learner" | "teacher" | "parent" | "admin"}>
      <div className="mx-auto w-full max-w-5xl px-4 pb-16 pt-6">
        <header className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Downloads</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Save lessons, videos, quizzes, past papers, assignments and AI notes to this device. Keep learning with no
            internet — everything you do syncs itself when you reconnect.
          </p>
        </header>

        <SyncStatusPanel className="mt-4" />

        <section className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><CloudDownload className="h-4 w-4" /> Saved items</div>
            <div className="mt-1 text-2xl font-semibold">{summary.count}</div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><HardDrive className="h-4 w-4" /> On this device</div>
            <div className="mt-1 text-2xl font-semibold">{bytes(summary.storedBytes)}</div>
            <div className="text-[11px] text-muted-foreground">
              {summary.savedPct > 0 ? `${summary.savedPct}% smaller after compression` : "Compressed for small data plans"}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><HardDrive className="h-4 w-4" /> Space available</div>
            <div className="mt-1 text-2xl font-semibold">{storage ? bytes(storage.quota - storage.usage) : "—"}</div>
          </div>
        </section>

        {/* Kind tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          {KINDS.map((k) => {
            const Icon = k.icon;
            const count = packs.filter((p) => p.kind === k.key).length;
            return (
              <button
                key={k.key}
                onClick={() => setTab(k.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  tab === k.key ? "border-foreground bg-foreground text-background" : "border-border hover:bg-accent"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {k.label}
                {count > 0 && <span className="rounded-full bg-current/10 px-1.5 text-[10px]">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Catalogue */}
        <section className="mt-4 rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg font-semibold">Available to download</h2>
            {visible.length > 0 && (
              <button
                onClick={downloadAllVisible}
                disabled={bulkBusy || state === "offline"}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
              >
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />}
                Download this set
              </button>
            )}
          </div>

          {loading ? (
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : state === "offline" ? (
            <p className="mt-3 text-sm text-muted-foreground">You're offline — reconnect to browse new content. Your saved items below still work.</p>
          ) : visible.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Nothing here yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {visible.map((item) => (
                <li key={`${item.kind}:${item.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{item.title}</div>
                    {item.subtitle && <div className="truncate text-[11px] text-muted-foreground">{item.subtitle}</div>}
                  </div>
                  <PackDownloadButton
                    kind={item.kind}
                    resourceId={item.id}
                    userId={userId}
                    includeMedia={item.hasMedia}
                    compact
                    onChange={() => void refreshLocal()}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Saved on device */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Saved on this device</h2>
          {packs.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nothing downloaded yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {packs.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {p.kind.replace("_", " ")} · {bytes(p.size_bytes)} · saved {new Date(p.downloaded_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={async () => { await removePack(p.kind, p.resource_id); await refreshLocal(); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs hover:bg-accent"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
